import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Alert,
    Chip,
    Paper,
} from '@mui/material';
import { Close, Add, Warning } from '@mui/icons-material';
import type { Option } from '../../types/product';

interface AddVariantModalProps {
    open: boolean;
    onClose: () => void;
    options: Option[];
    existingVariants: Array<{ attributes: { name: string; value: string }[] }>;
    onSave: (attrs: { name: string; value: string }[]) => void;
    baseSku?: string;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const variantKeyFromAttrs = (attrs: { name: string; value: string }[]) =>
    attrs
        .filter(a => a?.name && a?.value)
        .map(a => `${norm(a.name)}=${norm(a.value)}`)
        .sort()
        .join("|");

const makeAttrs = (pairs: { name: string; value: string }[]) =>
    pairs
        .filter(p => p.name && p.value)
        .map(p => ({ name: p.name.trim(), value: p.value.trim() }))
        .sort((a, b) => a.name.localeCompare(b.name));

const AddVariantModal: React.FC<AddVariantModalProps> = ({
    open,
    onClose,
    options,
    existingVariants,
    onSave,
    baseSku = "fc",
}) => {
    const [selections, setSelections] = useState<Record<number, string>>({});
    const [duplicateError, setDuplicateError] = useState(false);

    useEffect(() => {
        if (open) {
            setSelections({});
            setDuplicateError(false);
        }
    }, [open]);

    const handleSelect = (idx: number, value: string) => {
        setSelections((prev) => ({ ...prev, [idx]: value }));
        setDuplicateError(false);
    };

    const buildCleanAttrs = () =>
        options.map((o, idx) => ({
            name: (o.name || "").trim(),
            value: (selections[idx] || "").trim(),
        }));

    const allOptionsSelected = options.every((o, idx) => o.name && selections[idx]);

    const selectedAttrs = buildCleanAttrs()
        .map((a, idx) => ({ ...a, _idx: idx }))
        .filter((a) => a.value);


    const checkIfExists = () => {
        const attrs = buildCleanAttrs();
        const key = variantKeyFromAttrs(attrs);
        return existingVariants.some(v =>
            variantKeyFromAttrs(makeAttrs(v.attributes)) === key
        );
    };

    const generatePreviewSku = () => {
        const cleanAttrs = buildCleanAttrs();
        if (cleanAttrs.length === 0 || cleanAttrs.some(a => !a.value)) return `${baseSku}-variant`;
        const suffix = cleanAttrs
            .map(a => norm(a.value).replace(/\s+/g, "-"))
            .join("-");
        return `${baseSku}-${suffix}`.replace(/--+/g, "-");
    };


    const handleSave = () => {
        if (!allOptionsSelected) return;

        if (checkIfExists()) {
            setDuplicateError(true);
            return;
        }

        const cleanAttrs = buildCleanAttrs();
        onSave(cleanAttrs);
        handleClose();
    };

    const handleClose = () => {
        setSelections({});
        setDuplicateError(false);
        onClose();
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 2,
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Add Custom Variant
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Create a specific combination
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {options.map((option, idx) => (
                        <FormControl key={idx} fullWidth size="small">
                            <InputLabel>{option.name || `Option ${idx + 1}`}</InputLabel>
                            <Select
                                value={selections[idx] || ""}
                                label={option.name || `Option ${idx + 1}`}
                                onChange={(e) => handleSelect(idx, e.target.value as string)}
                                sx={{ "& .MuiSelect-select": { py: 1.5 } }}
                            >
                                {option.values.map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ))}


                    {duplicateError && (
                        <Alert
                            severity="error"
                            icon={<Warning />}
                            sx={{ mt: 1 }}
                        >
                            This variant already exists
                        </Alert>
                    )}

                    {allOptionsSelected && !duplicateError && selectedAttrs.length > 0 && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mt: 1,
                                bgcolor: 'action.hover',
                                borderRadius: 1.5,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Preview
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {selectedAttrs.map((attr) => (
                                    <Chip
                                        key={attr._idx}
                                        label={`${attr.name}: ${attr.value}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}

                            </Box>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                SKU will be generated as: {generatePreviewSku()}
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={handleClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!allOptionsSelected || duplicateError}
                    startIcon={<Add />}
                    sx={{
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                    }}
                >
                    Add Variant
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddVariantModal;